import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { MemberLite } from '../modules/Members/types/response/MemberLite';
import { MemberSummary } from '../modules/Members/types/response/Summary';

type PageIndex = number;

export interface CursorPage<T> {
	items: T[];
}

export interface MembersState {
	// view
	page: number;
	rowsPerPage: number;

	// cursor paging
	cursors: Array<number | null>; // cursors[0] = null (start)
	pageCache: Record<PageIndex, CursorPage<MemberLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;
	virtualCount: number; // UX count

	// summary
	summary?: MemberSummary | null;
}

const initialState: MembersState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const slice = createSlice({
	name: 'members',
	initialState,
	reducers: {
		resetMembersState: () => initialState,

		// when filters or rowsPerPage change, reset cursor paging
		resetMembersCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setMembersPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setMembersRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		// store a page result + next cursor
		setMembersPageData(
			state,
			action: PayloadAction<{ page: number; items: MemberLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;

			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;

			// the cursor to fetch NEXT page:
			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoadedPage = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoadedPage] != null;
			state.virtualCount = (maxLoadedPage + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setMembersSummary(state, action: PayloadAction<MemberSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetMembersState,
	resetMembersCursorPaging,
	setMembersPage,
	setMembersRowsPerPage,
	setMembersPageData,
	setMembersSummary,
} = slice.actions;

export default slice.reducer;

// Selectors
export const selectMembersPage = (s: RootState) => s.members.page;
export const selectMembersRowsPerPage = (s: RootState) => s.members.rowsPerPage;
export const selectMembersSummary = (s: RootState) => s.members.summary;

export const selectMembersItems = (s: RootState) => {
	const p = s.members.page;
	return s.members.pageCache[p]?.items ?? [];
};
export const selectMembersCount = (s: RootState) => s.members.virtualCount;
export const selectMembersCursorForPage = (p: number) => (s: RootState) =>
	s.members.cursors[p] ?? null;
export const selectMembersHasCachedPage = (p: number) => (s: RootState) =>
	Boolean(s.members.pageCache[p]);
